import type { HousingDto, PhotoItemDto } from '@customTypes/types';
import { ClubPhotoTag, PaymentFrequency } from '@customTypes/types';
import type { FileWithPath } from '@mantine/dropzone';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { notificationStyleConfig } from '@/features/notification/config/notificationConfig';
import {
  useClubProfilePhotoListQuery,
  useDeletePhotoItemMutation,
  useUploadPhotoMutation
} from '@/shared/api/requests';
import {
  useClubProfileHousingMutation,
  useClubProfileHousingQuery
} from '@/shared/api/requests/club';
import { isPhotoItemArrayDto } from '@/shared/constants/type-guards';

export const useHousingPage = () => {
  const { formatMessage } = useIntl();
  const { id: clubId } = useParams({ from: '/clubOwner/$id' });
  const housingQuery = useClubProfileHousingQuery({ clubId });
  const deletePhotoMutation = useDeletePhotoItemMutation();
  const queryClient = useQueryClient();
  const housingMutation = useClubProfileHousingMutation();
  const profilePhoto = useClubProfilePhotoListQuery({ profileId: clubId });
  const housingPhotosMutation = useUploadPhotoMutation({
    config: {
      headers: {
        'Content-Type': 'formData'
      }
    }
  });
  const navigate = useNavigate({ from: '/clubOwner/$id/onboard/$step' });

  const [housingImages, setHousingImages] = useState<
    FileWithPath[] | PhotoItemDto[]
  >();

  const form = useForm<HousingDto>({
    initialValues: {
      isHousingPaidByClub: false,
      isUtilityFeePaidByClub: false,
      utilityFee: 0,
      utilityPaymentFrequency: PaymentFrequency.Daily,
      isClubHousingMandatory: false,
      peoplePerRoom: 0,
      housingPrice: 0,
      housingPaymentFrequency: PaymentFrequency.Daily
    },
    transformValues: (values) => ({
      isHousingPaidByClub: values.isHousingPaidByClub,
      isUtilityFeePaidByClub: values.isUtilityFeePaidByClub,
      isClubHousingMandatory: values.isClubHousingMandatory,
      peoplePerRoom: values.peoplePerRoom,
      housingPrice: values.isHousingPaidByClub ? 0 : values.housingPrice,
      housingPaymentFrequency: values.isHousingPaidByClub
        ? PaymentFrequency.Daily
        : values.housingPaymentFrequency,
      utilityFee: values.isUtilityFeePaidByClub ? 0 : values.utilityFee,
      utilityPaymentFrequency: values.isUtilityFeePaidByClub
        ? PaymentFrequency.Daily
        : values.utilityPaymentFrequency
    })
  });

  const next = () => {
    navigate({
      to: '/clubOwner/$id/onboard/$step',
      params: {
        step: 'penalties'
      }
    });
  };

  useEffect(() => {
    if (housingQuery.isSuccess && housingQuery.data.data) {
      form.initialize({
        ...housingQuery.data.data
      });
    }

    if (profilePhoto.isSuccess && profilePhoto.data.data.length) {
      const housingPhotos = profilePhoto.data?.data.filter(
        (item) => item.tag === ClubPhotoTag.Housing
      );
      if (housingPhotos.length) {
        setHousingImages(housingPhotos);
      }
    }
  }, [housingQuery.data?.data, profilePhoto.data?.data]);

  const onSubmit = form.onSubmit(async (values) => {
    if (housingImages?.length && !isPhotoItemArrayDto(housingImages)) {
      if (profilePhoto?.data?.data) {
        const housingTagedPhoto = profilePhoto.data.data.filter(
          (item) => item.tag === ClubPhotoTag.Housing
        );
        housingTagedPhoto.forEach(async (item) => {
          await deletePhotoMutation.mutateAsync({
            params: {
              photoId: item.id.toString()
            }
          });
        });
      }
      housingImages.forEach(async (item) => {
        const formData = new FormData();
        formData.append('File', item);
        formData.append('Tag', ClubPhotoTag.Housing);
        formData.append('ClubProfileId', clubId);

        await housingPhotosMutation.mutateAsync({
          params: {
            formData
          }
        });
      });
    }

    await housingMutation.mutateAsync(
      {
        params: {
          data: values,
          clubId
        }
      },
      {
        onSuccess: async () => {
          notifications.show({
            title: formatMessage({ id: 'notifications.successDataUpdate' }),
            ...notificationStyleConfig
          });
          await queryClient.invalidateQueries({
            queryKey: ['ClubProfileHousing', clubId]
          });
          await queryClient.invalidateQueries({
            queryKey: ['ClubProfilePhotoList', clubId]
          });
          next();
        }
      }
    );
  });

  const queries = [profilePhoto];
  const isLoading = queries.some((q) => q.isLoading);

  return {
    state: { isLoading, form, housingImages },
    functions: { onSubmit, setHousingImages }
  };
};
